/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * auth-redirect.ts
 *
 * One implementation of the sign-in and sign-out redirects, shared by every call
 * site (cart.tsx, list.tsx, layouts/dashboard.tsx, app-sidebar.tsx,
 * thank-you.tsx, http-client.ts, api-config.ts).
 *
 * TA-6 (S576): the sign-in URL was built as `${base}${path}` with path defaulting
 * to /shop/, and the website has no /shop/ route -- every customer clicking Sign
 * in reached Page Not Found. The path is now /auth, written in the code.
 *
 * EX-10 (S576): VITE_AUTH_LOGIN_PATH is deliberately no longer read. It is set to
 * /shop/ in the deployed environments -- that was the whole point of making it
 * configurable -- so any code that falls back to /auth only when the variable is
 * empty would never reach the fallback, and every customer would still be sent to
 * a page that does not exist. The variable existed to make a one-off cutover
 * configurable; after the cutover it is only a way to break sign-in.
 *
 * Three properties here are load-bearing and should survive later edits:
 *  - safeReturnTo rejects any return address that is not on the shop's own
 *    origin, so a crafted ?redirect= cannot send a customer elsewhere holding a
 *    fresh token.
 *  - clearAuthToken runs before every redirect, so on a shared machine one
 *    customer's session cannot survive into the next customer's visit.
 *  - The base URL falls back through several sources, so no environment has to
 *    change on the day this ships.
 */

import configuration from "@/config/configuration";
import {
  clearAuthToken,
  clearSignedInState,
  revokeStoredRefreshToken,
} from "@/lib/token-cache";
import { logger } from "@/lib/logger";

/** Written in the code, not configured. See EX-10 above. */
const AUTH_LOGIN_PATH = "/auth";
const AUTH_LOGOUT_PATH = "/auth/logout";
const AUTH_PASSWORD_PATH = "/auth/forgot-password";

/**
 * Validate that a returnTo URL is same-origin as the current page.
 * Returns the safe URL string if valid, undefined otherwise.
 */
function safeReturnTo(returnTo?: string): string | undefined {
  if (!returnTo) return undefined;
  try {
    const u = new URL(returnTo, window.location.origin);
    return u.origin === window.location.origin ? u.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * VM-4 (S787): the origins a return address after the cart's own sign-out may
 * point at -- the configured Deskshare website addresses, nothing else.
 */
function allowedWebsiteOrigins(): Set<string> {
  const origins = new Set<string>();
  for (const raw of [configuration.deskshare_website_url, configuration.auth_frontend_url]) {
    const value = String(raw || "").trim();
    if (!value) continue;
    try {
      origins.add(new URL(value).origin);
    } catch {
      // A misconfigured value allows nothing.
    }
  }
  return origins;
}

/**
 * VM-4 (S787): validate a return address for after the cart's own sign-out.
 *
 * Accepted: an absolute address whose origin is exactly a configured Deskshare
 * website origin. What must still not pass, so a crafted link cannot sign a
 * customer out and then send them somewhere else: another site; a look-alike
 * host (deskshare.com.evil.example); a javascript: or data: address; a relative
 * address; this shop's own origin (the website sends the browser HERE, so the
 * shop is never where it goes back to); the website's own sign-out page in any
 * language (that would loop); anything when no website is configured.
 */
/**
 * Whether a path is the website's sign-out page, however it is spelled: the
 * path is percent-decoded, empty segments and ";parameters" are dropped, and
 * it is that page when its last two segments are "auth" and "logout" -- with
 * any language prefix, or several. "/en/auth/logout-help" or "/auth" (sign-in)
 * are not it. A path that cannot be decoded is treated as it.
 */
function isWebsiteSignOutPath(pathname: string): boolean {
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    return true;
  }
  const segments = decoded
    .toLowerCase()
    .split("/")
    .map((segment) => segment.split(";")[0])
    .filter(Boolean);
  const n = segments.length;
  return n >= 2 && segments[n - 2] === "auth" && segments[n - 1] === "logout";
}

export function safeWebsiteReturnTo(returnTo?: string): string | undefined {
  if (!returnTo) return undefined;
  const allowed = allowedWebsiteOrigins();
  if (allowed.size === 0) return undefined;
  try {
    const u = new URL(returnTo);
    if (u.origin === window.location.origin) return undefined;
    if (!allowed.has(u.origin)) return undefined;
    // Never back to the website's own sign-out page, in any language: the
    // website sends the browser HERE after signing out, so returning there
    // would start the loop this page exists to avoid.
    if (isWebsiteSignOutPath(u.pathname)) return undefined;
    return u.href;
  } catch {
    return undefined;
  }
}

/** The Deskshare website origin that serves /auth, sign-out and JWT minting. */
function websiteBaseUrl(): string {
  return (
    configuration.deskshare_website_url ||
    configuration.auth_frontend_url ||
    "https://deskshare.com"
  ).replace(/\/$/, "");
}

/**
 * A1 (FCR S687): the authentication service API origin lives here, with the
 * other Deskshare addresses.
 *
 * This file already owns the question "which Deskshare address does this point
 * at" -- desksharePath(), detectLanguage(), buildAuthLoginUrl(),
 * buildDeskshareLogoutUrl() and buildPasswordResetUrl() -- and it is already
 * where the rule lives that the sign-in path is written in code rather than in
 * an environment variable. Putting a fourth address-builder in token-cache.ts
 * (whose job is tokens) would split knowledge this shop has deliberately
 * concentrated once already.
 *
 * Item 3 (S687): returns `null` rather than falling back to the marketing
 * website. VITE_DESKSHARE_WEBSITE_URL is the React site customers browse; it has
 * never served /api/auth/* and never will -- those routes live in the Deskshare
 * Website's Django backend at a different origin. A fallback would therefore
 * guarantee a 404 on every renewal in an environment that looked correctly
 * configured, which is worse than not having the feature. An environment that
 * has not been configured for renewable sessions simply does not have them:
 * the customer's session ends after an hour, exactly as it does today.
 */
export function authServiceBaseUrl(): string | null {
  const base = String(configuration.auth_service_url || "")
    .trim()
    .replace(/\/$/, "");
  return base || null;
}

/**
 * Build a website path with the correct language prefix.
 *
 * TA-6: the website has NO /en/ prefix for English. German is /de/auth; English
 * is /auth, not /en/auth. Any builder that adds the prefix uniformly produces a
 * Page Not Found for the majority language, which is why this is a real outage
 * rather than a cosmetic problem.
 */
export function desksharePath(path: string, lang?: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const language = (lang ?? detectLanguage()).toLowerCase();
  if (!language || language === "en") return normalizedPath;
  return `/${language}${normalizedPath}`;
}

/**
 * Read the language from the shop's own address, e.g. /de/cart -> "de".
 *
 * B-5 (S620): exported because the checkout request needs the same answer. The
 * purchase email's product page link is built on the server, so the server has
 * to be told which language the customer was buying in -- and this is already
 * the one place that knows. A second reader of the address would be a second
 * copy of the same rule (English takes no prefix), which is the drift this item
 * exists to remove rather than repeat.
 */
export function detectLanguage(): string {
  try {
    const first = window.location.pathname.split("/").filter(Boolean)[0] ?? "";
    return /^[a-z]{2}$/i.test(first) ? first.toLowerCase() : "en";
  } catch {
    return "en";
  }
}

/**
 * Build the website sign-in URL with an optional same-origin returnTo.
 *
 * SU-7 (FCR S732): and an optional same-origin `cancelTo` -- where the sign-in
 * page sends a customer who gives up. The desktop purchase window has no
 * browser Back button, so without it the only way out of the sign-in page was
 * to close the window. Both addresses pass the same same-origin check; a
 * cancel address on another site is left out, never sent. The website shows
 * "Back to cart" when it is present; until it does, it ignores the parameter.
 */
export function buildAuthLoginUrl(returnTo?: string, cancelTo?: string): string {
  const base = websiteBaseUrl();
  const path = desksharePath(AUTH_LOGIN_PATH);
  const safe = safeReturnTo(returnTo);
  const safeCancel = safeReturnTo(cancelTo);

  // Request from the DS Controls correction run (Session 604): the language is
  // stated for EVERY language, English included. The website has no /en/
  // prefix, so plain /auth says nothing, and the website then falls back to
  // the language its own visit last stored -- an English customer could land on
  // a French sign-in page. The website obeys ?lang= over its stored language.
  const params: string[] = [`lang=${encodeURIComponent(detectLanguage())}`];
  if (safe) params.push(`redirect=${encodeURIComponent(safe)}`);
  if (safeCancel) params.push(`cancel=${encodeURIComponent(safeCancel)}`);
  return `${base}${path}?${params.join("&")}`;
}

/**
 * SU-7 (FCR S732): the marker a cart address carries when the customer came
 * back from the sign-in page WITHOUT signing in. The cart then shows its sign-in
 * prompt instead of sending them straight back to the page they just left.
 */
export const LOGIN_CANCELLED_PARAM = "login_cancelled";

/** The same address with the login-cancelled marker added (or removed). */
export function withLoginCancelledMarker(address: string, present = true): string {
  try {
    const url = new URL(address, window.location.origin);
    if (present) url.searchParams.set(LOGIN_CANCELLED_PARAM, "1");
    else url.searchParams.delete(LOGIN_CANCELLED_PARAM);
    return url.href;
  } catch {
    return address;
  }
}

/**
 * Build the website sign-out URL.
 *
 * TA-6: signing out of the shop alone left the website session intact, so the
 * next visit signed the customer straight back in -- which reads as a sign-out
 * button that does not work.
 */
export function buildDeskshareLogoutUrl(returnTo?: string): string {
  const base = websiteBaseUrl();
  const path = desksharePath(AUTH_LOGOUT_PATH);
  const safe = safeReturnTo(returnTo);

  return safe
    ? `${base}${path}?redirect=${encodeURIComponent(safe)}`
    : `${base}${path}`;
}

/**
 * Clear the stored auth token and redirect to the auth login page.
 *
 * Call this from every place that currently does:
 *   window.location.href = `${baseUrl}/shop/?redirect=...`
 *
 * Clearing the token before redirecting prevents a previous account's
 * session from leaking across users on shared devices.
 */
/**
 * Build the website password page URL.
 *
 * mode=set tells the website this customer has never had a password, so the page
 * reads "Create your password" rather than "Reset your password". Gate G-5
 * confirms the website honours it; if it does not, the link still works and the
 * email is still sent -- only the heading is wrong.
 */
export function buildPasswordResetUrl(token?: string): string {
  const base = websiteBaseUrl();
  const path = desksharePath(AUTH_PASSWORD_PATH);
  const query = token
    ? `?mode=set&token=${encodeURIComponent(token)}`
    : "?mode=set";
  return `${base}${path}${query}`;
}

/**
 * Item 8 (FCR S687): this function must NOT gain a revoke call.
 *
 * It is the FAILURE path -- it runs when a token could not be renewed. Revoking
 * here would destroy a whole refresh family on the strength of a transient
 * error, and N1 is about how dangerous this path already is without adding to
 * it. Sign-out is `authClient.signOut()` and `redirectToDeskshareLogout()`;
 * this is not sign-out.
 */
/**
 * ST-3 and N-2 (S712) -- sign-out and the sign-in guard used to race, and the
 * guard won.
 *
 * `handleLogout` clears the tokens and then navigates to the website's sign-out
 * page. Clearing them announces the change, the dashboard re-renders with no
 * session, and the guard's effect assigns `window.location.href` to the SIGN-IN
 * page with the current page as the return address. That assignment happens
 * after the sign-out navigation was requested, and the later assignment wins.
 *
 * Two customer-visible consequences, reported separately and caused by one race:
 *
 *   * ST-3 -- the next sign-in lands on whichever page the customer signed out
 *     from, which reads as "the dashboard always opens Account Settings";
 *   * N-2 -- the website's sign-out page never runs, so the WEBSITE session
 *     survives a dashboard sign-out. On a shared computer the next person is
 *     signed in as the previous customer.
 *
 * A module-level flag is simpler than reordering revoke, clear and navigate --
 * each of those three has a reason recorded beside it, and this needs none of
 * them to move. It is deliberately not React state: the guard's effect reads it
 * in the same tick the tokens are cleared, before any re-render could deliver a
 * new value.
 *
 * It is never unset. The page is leaving; the only thing that could clear it is
 * the navigation itself, and a sign-out that is somehow cancelled leaves the
 * customer on a page with no session, which is where the guard would have sent
 * them anyway.
 */
let signingOut = false;

export function beginSignOut(): void {
  signingOut = true;
}

export function isSigningOut(): boolean {
  return signingOut;
}

/** Test seam: the flag is module state, so a suite must be able to reset it. */
export function resetSignOutForTests(): void {
  signingOut = false;
}

export function redirectToAuthLogin(returnTo?: string): void {
  // S787: never while a sign-out is leaving the page. A request that answers
  // 401 after a sign-out has cleared the tokens (the cart's own sign-out page
  // runs while the app shell may still be fetching) would otherwise send a
  // customer who has just signed out to the SIGN-IN page, racing the sign-out's
  // own navigation. The dashboard and the cart already check the flag; this
  // covers every other caller, both 401 handlers among them.
  if (isSigningOut()) {
    logger.info("Sign-in redirect skipped: a sign-out is leaving the page");
    return;
  }
  clearAuthToken();
  const url = buildAuthLoginUrl(returnTo);
  logger.info("Redirecting to auth login", { url });
  window.location.href = url;
}

/**
 * What a signed-in visit leaves in this browser, and the one clean-up of it.
 * VM-3 (S787): both live in token-cache.ts, beside the tokens; re-exported here
 * so every existing import keeps working.
 */
export { SIGNED_IN_STORAGE_KEYS, clearSignedInState } from "@/lib/token-cache";

/**
 * Sign out of the shop AND the Deskshare website, then come back to `returnTo`
 * (this origin only) or, without one, land on the website's home page.
 *
 * The one sign-out every button uses -- the cart header and the dashboard
 * sidebar -- so the two cannot drift apart again. They did: the cart header
 * once ended only the shop's token, the website kept its own session, and the
 * next Log in signed the customer straight back in without asking.
 *
 * The order matters. The sign-out flag goes up before anything is cleared,
 * because clearing the token announces "no session" and a sign-in guard on the
 * page beneath (the dashboard's, or the cart's for credit, Extra Words and
 * upgrade purchases) would otherwise navigate to the sign-in page after this
 * navigation was requested, and the later navigation wins. Then the cart state,
 * then redirectToDeskshareLogout(): revoke, clear the token, navigate.
 */
export async function signOutEverywhere(returnTo?: string): Promise<void> {
  beginSignOut();
  clearSignedInState();
  await redirectToDeskshareLogout(returnTo);
}

/**
 * N-8 (FCR S732): sign out of THIS shop without leaving the page.
 *
 * Used when an account deletion's result is unknown: the dialog must stay on
 * screen to say so, so the website sign-out navigation is not wanted. The
 * refresh token is revoked first (as redirectToDeskshareLogout does, and for the
 * same reason), then the stored tokens are cleared.
 */
export async function signOutOfShopLocally(): Promise<void> {
  await revokeStoredRefreshToken();
  clearAuthToken();
}

/**
 * VM-2 (S787): end this cart's session and go back to the Deskshare website --
 * what the cart's own sign-out page does when the website sends the browser
 * there after its own sign-out. The cart keeps its sign-in in this origin's
 * storage, which the website cannot clear.
 *
 * The flag goes up first, so no sign-in guard can send the browser elsewhere
 * while this runs; then the cart state; then revoke BEFORE clearing (clearing
 * first would leave nothing to revoke). It never goes to the website's
 * /auth/logout -- the website has already signed out and sent the browser here,
 * so going back there would loop. window.location.replace keeps this page out of
 * the history, so Back never returns to it.
 */
export async function signOutOfShopAndReturn(returnTo?: string): Promise<void> {
  beginSignOut();
  clearSignedInState();
  await revokeWithinSignOutLimit();
  clearAuthToken();
  window.location.replace(safeWebsiteReturnTo(returnTo) ?? `${websiteBaseUrl()}/`);
}

/**
 * S787: the longest a sign-out waits for the refresh token's revoke before it
 * leaves the page. The revoke goes first whenever it answers (it normally
 * answers in well under a second); a revoke that never answers must not leave
 * the customer on a loading screen in the middle of signing out. The stored
 * tokens are cleared either way.
 */
export const SIGN_OUT_REVOKE_WAIT_MS = 5_000;

function revokeWithinSignOutLimit(): Promise<void> {
  return Promise.race([
    revokeStoredRefreshToken(),
    new Promise<void>((resolve) => setTimeout(resolve, SIGN_OUT_REVOKE_WAIT_MS)),
  ]);
}

export async function redirectToDeskshareLogout(returnTo?: string): Promise<void> {
  // Item 8 (FCR S687): revoke BEFORE clearing, and before navigating.
  //
  // Correction 3: this function used to return void and end with
  // `window.location.href = url`. A caller that did not wait would navigate
  // before the revoke request had left the browser and the browser would cancel
  // it -- so the fix would appear to be in place and do nothing. The navigation
  // is inside the promise now, after the await, so it cannot outrun the request
  // whether or not the caller waits.
  //
  // The revoke is idempotent and the service always answers 200 whether or not
  // the credential was still valid, so a caller that has already revoked costs
  // one request that finds nothing to revoke, and never an error the customer sees.
  await revokeWithinSignOutLimit();
  clearAuthToken();
  const url = buildDeskshareLogoutUrl(returnTo);
  logger.info("Redirecting to website logout", { url });
  window.location.href = url;
}

/**
 * ST-7 (FCR S733): the purchases the cart signs a customer in for, and so the
 * sentence it shows when they came back from the sign-in page without signing
 * in. Each is a key under `cart.signIn.reason` in every locale.
 */
export type SignInReason = "credits" | "extraWords" | "upgrade";

/** The reason for this visit, in the cart's order of precedence; null if none. */
export function signInReasonFor(flows: {
  credits: boolean;
  extraWords: boolean;
  upgrade: boolean;
}): SignInReason | null {
  if (flows.credits) return "credits";
  if (flows.extraWords) return "extraWords";
  if (flows.upgrade) return "upgrade";
  return null;
}
